import React from 'react';
import type { match } from '@shared/core';
import { Redirect, Route, Switch } from '@shared/core';
import { isValidShopPageMatch, useCardsRouterContext } from '@apps/card/Card.routes';
import { DesignsGallery } from './DesignsGallery';
import { ExistingDraftsWarningProvider } from '@apps/card/components/ExistingDraftsWarning';
import { RestrictedInternationalShippingWarningProvider } from '@apps/card/components/RestrictedInternationalShippingWarning';
import { ProductDetailsPage } from '../ProductDetailsPage/ProductDetailsPage';
import type { CustomizeNowClickOverrideHandler } from '@apps/card/types/stationeryTemplate.types';
import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { CategoryNavSwitch } from '../CategoryNav';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { CardDiscountProvider } from '@apps/card/contexts/useCardDiscountContext';
import { SearchProvider } from './components/Search/SearchContext';
import { WebsiteThemeIdProvider } from '@apps/card/contexts/WebsiteThemeIdContext';
import { GallerySurveyTrigger } from '../GallerySurveyTrigger/GallerySurveyTrigger.lazy';

interface ShopProps {
  eventId?: string;
  onCustomizeClickCardOverride?: CustomizeNowClickOverrideHandler;
  match: match;
}

export const ShopRouter = (props: ShopProps) => {
  const { eventId, onCustomizeClickCardOverride, match } = props;
  const { routes, getShopPath } = useCardsRouterContext();
  const { currentCategory } = useCardCategoryContext();

  if (!isValidShopPageMatch(match.params)) {
    // TODO: Discuss with team about showing a 404 page or redirecting to a known shop page
    return <Redirect to={getShopPath('wedding', StationeryTemplateCategoryEnum.saveTheDate)} />;
  }

  return (
    <CardDiscountProvider>
      <RestrictedInternationalShippingWarningProvider eventId={eventId}>
        <ExistingDraftsWarningProvider eventId={eventId}>
          <Switch>
            <Route
              path={routes.productDetailsPageV2.paths}
              render={({ match }) => (
                <ProductDetailsPage
                  eventId={props.eventId}
                  themeId={match.params.themeId}
                  templateId={match.params.templateId}
                  onCustomizeCardClickOverride={onCustomizeClickCardOverride}
                />
              )}
            />
            {/* Legacy routing for pdp prior to including templateId in the URL  */}
            <Route
              path={routes.productDetailsPage.paths}
              render={({ match }) => (
                <ProductDetailsPage
                  eventId={props.eventId}
                  themeId={match.params.themeId}
                  templateId={null}
                  onCustomizeCardClickOverride={onCustomizeClickCardOverride}
                />
              )}
            />
            <Route
              exact={true}
              path={routes.designsGallery.paths}
              render={({ match }) => (
                <>
                  <CategoryNavSwitch />
                  {eventId && <GallerySurveyTrigger />}
                  <SearchProvider>
                    <WebsiteThemeIdProvider eventHandle={match.params.eventHandle}>
                      <DesignsGallery
                        onCustomizeCardClickOverride={onCustomizeClickCardOverride}
                        eventId={eventId}
                        eventHandle={match.params.eventHandle}
                      />
                    </WebsiteThemeIdProvider>
                  </SearchProvider>
                </>
              )}
            />
            <Redirect to={getShopPath('wedding', currentCategory)} />
          </Switch>
        </ExistingDraftsWarningProvider>
      </RestrictedInternationalShippingWarningProvider>
    </CardDiscountProvider>
  );
};
